/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type ChangeEvent, type FC, useCallback, useId } from "react";
import {
  InlineField,
  Label,
  RadioControl,
  Separator,
} from "@vector-im/compound-web";
import { useTranslation } from "react-i18next";

import {
  cameraFrameRate,
  cameraFrameRates,
  type CameraFrameRate,
  cameraResolution,
  cameraResolutions,
  type CameraResolution,
  useSetting,
} from "./settings";
import {
  cameraCaptureStatus$,
  cameraPreferencesEqual,
  cameraSourceMeetsPreferences,
  getCameraTarget,
} from "../livekit/cameraPreferences";
import { useBehavior } from "../useBehavior";
import styles from "./CameraPreferences.module.css";

export const CameraPreferences: FC = () => {
  const { t } = useTranslation();
  const resolutionGroupId = useId();
  const frameRateGroupId = useId();
  const resolutionDescriptionId = useId();
  const frameRateDescriptionId = useId();
  const [resolution, setResolution] = useSetting(cameraResolution);
  const [frameRate, setFrameRate] = useSetting(cameraFrameRate);
  const captureStatus = useBehavior(cameraCaptureStatus$);
  const requested = { resolution, frameRate };

  const onResolutionChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setResolution(Number(event.target.value) as CameraResolution);
    },
    [setResolution],
  );
  const onFrameRateChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setFrameRate(Number(event.target.value) as CameraFrameRate);
    },
    [setFrameRate],
  );

  let statusMessage: string | null = null;
  let statusIsWarning = false;
  if (
    captureStatus !== null &&
    cameraPreferencesEqual(captureStatus.requested, requested)
  ) {
    const target = getCameraTarget(captureStatus.requested.resolution);
    if (captureStatus.kind === "error") {
      statusIsWarning = true;
      statusMessage = t("settings.camera_quality.capture_error", {
        width: target.width,
        height: target.height,
        frameRate: captureStatus.requested.frameRate,
      });
    } else {
      const {
        width,
        height,
        frameRate: actualFrameRate,
      } = captureStatus.actual;
      if (
        width !== undefined &&
        height !== undefined &&
        actualFrameRate !== undefined
      ) {
        statusIsWarning = !cameraSourceMeetsPreferences(
          captureStatus.actual,
          requested,
        );
        statusMessage = t(
          statusIsWarning
            ? "settings.camera_quality.capture_limited"
            : "settings.camera_quality.capture_active",
          {
            width,
            height,
            frameRate: Math.round(actualFrameRate * 100) / 100,
          },
        );
      }
    }
  }

  return (
    <section className={styles.preferences}>
      <fieldset
        className={styles.group}
        aria-describedby={resolutionDescriptionId}
      >
        <legend className={styles.legend}>
          {t("settings.camera_quality.quality_header")}
        </legend>
        <p id={resolutionDescriptionId} className={styles.description}>
          {t("settings.camera_quality.description")}
        </p>
        <div className={styles.options}>
          {cameraResolutions.map((option) => {
            const target = getCameraTarget(option);
            return (
              <InlineField
                key={option}
                name={resolutionGroupId}
                control={
                  <RadioControl
                    checked={resolution === option}
                    onChange={onResolutionChange}
                    value={option}
                  />
                }
              >
                <Label>
                  {t("settings.camera_quality.resolution_option", {
                    height: target.height,
                    width: target.width,
                  })}
                </Label>
              </InlineField>
            );
          })}
        </div>
      </fieldset>

      <Separator className={styles.separator} />
      <fieldset
        className={styles.group}
        aria-describedby={frameRateDescriptionId}
      >
        <legend className={styles.legend}>
          {t("settings.camera_quality.frame_rate_header")}
        </legend>
        <p id={frameRateDescriptionId} className={styles.description}>
          {t("settings.camera_quality.frame_rate_description")}
        </p>
        <div className={styles.options}>
          {cameraFrameRates.map((option) => (
            <InlineField
              key={option}
              name={frameRateGroupId}
              control={
                <RadioControl
                  checked={frameRate === option}
                  onChange={onFrameRateChange}
                  value={option}
                />
              }
            >
              <Label>
                {t("settings.camera_quality.frame_rate_option", {
                  frameRate: option,
                })}
              </Label>
            </InlineField>
          ))}
        </div>
      </fieldset>

      <p className={styles.description}>
        {t("settings.camera_quality.advanced_precedence")}
      </p>

      {statusMessage !== null && (
        <p
          className={statusIsWarning ? styles.warning : styles.status}
          role={statusIsWarning ? "alert" : "status"}
        >
          {statusMessage}
        </p>
      )}
    </section>
  );
};
